import { useOptionallyControlledState } from '@ipfabric/ui-components/utils/react/useOptionallyControlledState';
import { constNull } from 'fp-ts/lib/function';
import type { ReactNode } from 'react';

export interface Tab<TKey> {
  disabled?: boolean;
  key: TKey;
  label: ReactNode;
  render: () => ReactNode;
}

export interface TabsProps<TKey> {
  /**
   * Active tab for controlled component variant
   *
   * Null advised only for all disabled tabs case
   */
  activeTab?: TKey | null;
  tabs: readonly Tab<TKey>[];
  onChangeTab?: (key: TKey) => void;
  /**
   * Initial active tab for uncontrolled component variant
   *
   * If not present, defaults to first non-disabled tab.
   */
  initialActiveTab?: TKey;
}

export interface TabsHook<TKey> {
  activeTab: TKey | null;
  tabs: readonly Tab<TKey>[];
  renderActiveTab: () => ReactNode;
  handleChangeTab: (key: TKey) => void;
}

export const useTabs = <TKey extends string = string>(
  props: TabsProps<TKey>,
): TabsHook<TKey> => {
  const [activeTab, setActiveTab] = useOptionallyControlledState<TKey | null>(
    props.activeTab,
    // we know we won't ever fire null from inside of this component,
    // but we have to satisfy useOptionallyControlledState's signature
    props.onChangeTab as (key: TKey | null) => null,
    props.initialActiveTab ??
      props.tabs.find((tab) => !tab.disabled)?.key ??
      null,
  );

  const renderActiveTab =
    props.tabs.find((tab) => tab.key === activeTab)?.render ?? constNull;

  const handleChangeTab = (key: TKey) => {
    setActiveTab(key);
  };

  return {
    activeTab,
    tabs: props.tabs,
    renderActiveTab,
    handleChangeTab,
  };
};
